import {UserInManagementRoles} from "./../../helpers/userRoles";
import {makeAutoObservable} from "mobx";
import {UserRoles} from "../../helpers/userRoles";

export interface IUserStore {
    username: string;
    setUsername: (userName: string) => void;
    unreadAlertsCount: number;
    setUnreadAlertsCount: (unreadAlertsCount: number | undefined) => void;
    language: string;
    setLanguage: (language: string) => void;
    roles: UserRoles[];
    setRoles: (roles: UserRoles[]) => void;
    hasRole: (roles: UserRoles[]) => boolean;
    group: UserInManagementRoles | undefined;
    setGroup: (groups: UserInManagementRoles[]) => void;

    // dictionaries
    displayDictionaries: boolean;

    // timetable
    displayTimetable: boolean;

    // users
    displayUsersList: boolean;
    canModifyUser: boolean;
    canChangeOrResetPassword: boolean;

    // ticket
    displayTicket: boolean;
    canModifyTicket: boolean;
    canScanTicketBarcode: boolean;

    // order
    displayOrder: boolean;
    canModifyOrder: boolean;
    displayOrderStatus: boolean;
    displayOrderPhotos: boolean;
    canPrintOrderFile: boolean;
    canAssigningWagonNumber: boolean;

    // tcr
    displayTcr: boolean;
    canModifyTcr: boolean;
    canPrintTcrFile: boolean;

    // notifications (for tickets and orders)
    displayNotifications: boolean;

    // kioskPassageDirection (for tickets and orders)
    displayKioskPassageDirection: boolean;

    // topology
    displayTopology: boolean;

    // documents
    displayDocuments: boolean;

    // alerts
    displayAlerts: boolean;

    // prices list
    displayPriceList: boolean;

    // place
    displayOwnedVehicles: boolean;
    displayParkings: boolean;
    displayLightBoards: boolean;
    displayEntryExit: boolean;


    // reports
    displayReports: boolean;

    // own vehicle selection
    displayOwnVehicleSelection: boolean;
}

export class UserStore implements IUserStore {
    _username = "";
    _unreadAlertsCount = 0;
    _language = "pl";
    _roles: UserRoles[] = [];
    _group: UserInManagementRoles | undefined = undefined;

    constructor() {
        makeAutoObservable(this);
    }

    get username() {
        return this._username;
    }

    setUsername(username: string) {
        this._username = username;
    }

    get unreadAlertsCount() {
        return this._unreadAlertsCount;
    }

    setUnreadAlertsCount(unreadAlertsCount: number | undefined) {
        this._unreadAlertsCount = unreadAlertsCount || 0;
    }

    get language() {
        return this._language;
    }

    setLanguage(language: string) {
        this._language = language;
    }

    get roles() {
        return this._roles;
    }

    setRoles(roles: UserRoles[]) {
        this._roles = roles;
    }

    hasRole(roles: UserRoles[]) {
        return this._roles.some(role => roles?.includes(role));
    }

    get group() {
        return this._group;
    }

    setGroup(groups: UserInManagementRoles[]) {
        this._group = groups.find((group) => group !== UserInManagementRoles.helpdesk);
    }

    get displayDictionaries() {
        return this._roles.includes(UserRoles.dictionariesViewAndModification);
    }

    get displayTimetable() {
        return this._roles.includes(UserRoles.timetableViewAndModification);
    }

    get displayUsersList() {
        return this._roles.includes(UserRoles.usersView);
    }

    get displayTopology() {
        return this._roles.includes(UserRoles.topologyViewAndModification);
    }

    get displayTicket() {
        return this._roles.includes(UserRoles.ticketView);
    }

    get canScanTicketBarcode() {
        return this._roles.includes(UserRoles.ticketBarcodeScanning);
    }

    get displayOrder() {
        return this._roles.includes(UserRoles.orderView);
    }

    get displayOrderPhotos() {
        return this._roles.includes(UserRoles.orderPhotosView);
    }

    get displayNotifications() {
        return this._roles.includes(UserRoles.notificationsView);
    }

    get displayDocuments() {
        return this._roles.includes(UserRoles.documentsView);
    }

    get displayOwnedVehicles() {
        return this._roles.includes(UserRoles.ownedVehiclesView);
    }

    get displayParkings() {
        return this._roles.includes(UserRoles.parkingsView);
    }

    get displayLightBoards() {
        return this._roles.includes(UserRoles.lightBoardsViewAndModification);
    }

    get displayEntryExit() {
        return this._roles.includes(UserRoles.entryExitViewAndModification);
    }

    get displayReports() {
        return this._roles.includes(UserRoles.reportsView) ;
    }

    get canPrintTcrFile() {
        return this._roles.includes(UserRoles.tcrFilePrinting);
    }

    get displayTcr() {
        return this._roles.includes(UserRoles.tcrView);
    }

    get displayOwnVehicleSelection() {
        return this._roles.includes(UserRoles.ownVehicleSelection);
    }

    get canChangeOrResetPassword() {
        return this._roles.includes(UserRoles.userPasswordReset);
    }

    get canModifyUser() {
        return this._roles.includes(UserRoles.userModification);
    }

    get displayAlerts() {
        return this._roles.includes(UserRoles.alertsViewAndModification);
    }

    get displayOrderStatus() {
        return this._roles.includes(UserRoles.orderStatusViewAndModification);
    }

    get displayPriceList() {
        return this._roles.includes(UserRoles.priceListViewAndModification);
    }

    get canModifyTcr() {
        return this._roles.includes(UserRoles.tcrModification);
    }

    get canModifyOrder() {
        return this._roles.includes(UserRoles.orderModification);
    }

    get canModifyTicket() {
        return this._roles.includes(UserRoles.ticketModification);
    }

    get canPrintOrderFile() {
        return this._roles.includes(UserRoles.orderFilePrinting);
    }

    get canAssigningWagonNumber() {
        return this._roles.includes(UserRoles.assigningWagonNumber);
    }

    get displayKioskPassageDirection() {
        return this._roles.includes(UserRoles.kioskPassageDirection);
    }
}
